import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { reportsApi } from '../../utils/reportsApi';
import { api } from '../../utils/api';
import './ReportCommon.css';

function fmtDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toISOString().slice(0, 10);
}
function fmt(n) {
  const v = Number(n);
  if (!v) return '';
  return v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

const OutstandingVouchersPage = () => {
  const navigate = useNavigate();

  const [entityId, setEntityId] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo,   setDateTo]   = useState('');
  const [type,     setType]     = useState('');

  const [entities,  setEntities]  = useState([]);
  const [result,    setResult]    = useState(null);
  const [loading,   setLoading]   = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error,     setError]     = useState(null);

  useEffect(() => {
    api.get('/entities').then((d) => setEntities(d || [])).catch(() => {});
  }, []);

  const params = {
    entityId: entityId || undefined,
    dateFrom: dateFrom || undefined,
    dateTo:   dateTo   || undefined,
    type:     type     || undefined,
  };

  const run = async () => {
    setLoading(true); setError(null); setResult(null);
    try {
      setResult(await reportsApi.getOutstandingVouchers(params));
    } catch (e) {
      setError(e.message || 'Failed to run report');
    } finally { setLoading(false); }
  };

  const exportXlsx = async () => {
    setExporting(true);
    try { await reportsApi.exportOutstandingVouchers(params, 'outstanding-vouchers.xlsx'); }
    catch (e) { setError(e.message || 'Export failed'); }
    finally { setExporting(false); }
  };

  const items = result?.items ?? [];

  return (
    <div className="rpt-page">
      <div className="rpt-header">
        <button className="rpt-back" onClick={() => navigate('/reports')}><i className="fas fa-arrow-left"></i> Reports</button>
        <h1 className="rpt-title">Outstanding (Draft) Vouchers</h1>
      </div>

      <div className="rpt-filters">
        <label className="rpt-label">
          Entity
          <select value={entityId} onChange={(e) => setEntityId(e.target.value)} className="rpt-select">
            <option value="">All entities</option>
            {entities.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
          </select>
        </label>
        <label className="rpt-label">
          Type
          <select value={type} onChange={(e) => setType(e.target.value)} className="rpt-select" style={{ minWidth: 110 }}>
            <option value="">All types</option>
            <option value="JV">JV</option>
            <option value="RV">RV</option>
            <option value="PV">PV</option>
          </select>
        </label>
        <label className="rpt-label">
          From
          <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="rpt-input" />
        </label>
        <label className="rpt-label">
          To
          <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="rpt-input" />
        </label>
        <div className="rpt-filter-actions">
          <button className="rpt-btn rpt-btn--primary" onClick={run} disabled={loading}>
            {loading ? <><i className="fas fa-spinner fa-spin"></i> Running…</> : <><i className="fas fa-play"></i> Run</>}
          </button>
          {result && (
            <button className="rpt-btn rpt-btn--excel" onClick={exportXlsx} disabled={exporting}>
              {exporting ? <><i className="fas fa-spinner fa-spin"></i> Exporting…</> : <><i className="fas fa-file-excel"></i> Export Excel</>}
            </button>
          )}
        </div>
      </div>

      {error && <div className="rpt-error"><i className="fas fa-exclamation-circle"></i> {error}</div>}

      {result && (
        <div className="rpt-result">
          <div className="rpt-meta">
            <strong>{result.total}</strong> draft voucher{result.total !== 1 ? 's' : ''} pending posting
          </div>
          <div className="rpt-table-wrap">
            <table className="rpt-table">
              <thead>
                <tr>
                  <th>Type</th>
                  <th>#</th>
                  <th>Date</th>
                  <th>Doc #</th>
                  <th className="col-name">Description</th>
                  <th>Entity</th>
                  <th className="col-amt">Debit (EGP)</th>
                  <th className="col-amt">Credit (EGP)</th>
                  <th style={{ textAlign: 'right' }}>Lines</th>
                  <th>Created By</th>
                </tr>
              </thead>
              <tbody>
                {items.length === 0 && (
                  <tr><td colSpan="10" className="rpt-empty">No draft vouchers found.</td></tr>
                )}
                {items.map((v) => (
                  <tr key={v.id}>
                    <td><span className={`type-chip type-chip--${v.type?.toLowerCase()}`}>{v.type}</span></td>
                    <td>{v.voucherNumber}</td>
                    <td>{fmtDate(v.date)}</td>
                    <td>{v.docNumber || '—'}</td>
                    <td className="col-name">{v.description || '—'}</td>
                    <td>{v.entity?.name || 'All'}</td>
                    <td className="col-amt">{fmt(v.totalDebitLocal)}</td>
                    <td className="col-amt">{fmt(v.totalCreditLocal)}</td>
                    <td style={{ textAlign: 'right' }}>{v._count?.lines ?? 0}</td>
                    <td>{v.createdBy?.fullName || v.createdBy?.username || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <style>{`
        .type-chip { display:inline-block; padding:1px 8px; border-radius:999px; font-size:0.75rem; font-weight:600; }
        .type-chip--jv { background:#dbeafe; color:#1d4ed8; }
        .type-chip--rv { background:#dcfce7; color:#166534; }
        .type-chip--pv { background:#fef3c7; color:#92400e; }
      `}</style>
    </div>
  );
};

export default OutstandingVouchersPage;
